/**
 * I18N-C3-D1 — Sheet25 capability-action finite exact-string localization.
 *
 * Presentation only.
 *
 * Scope:
 * - cap1Action .. cap5Action only.
 *
 * Guarantees:
 * - Field-aware exact whole-string lookup only.
 * - No parsing, splitting, normalization, regex, substring replacement,
 *   inference, or AI translation.
 * - zh returns canonical source values verbatim.
 * - en translates only exact audited canonical values.
 * - Unknown field/value falls back to canonical verbatim.
 * - Does not import engine / DB mirrors / storage / migration / adapter.
 */

const EN_BY_FIELD = Object.freeze({
  cap1Action: Object.freeze({
    "聚焦一项可收费价值，明确客户、价格与每周获客动作。":
      "Focus on one chargeable value proposition, define the target customer and price, and set weekly customer-acquisition actions.",
  }),

  cap2Action: Object.freeze({
    "建立固定支出上限、应急储备与月度现金流复盘。":
      "Set a fixed spending ceiling, build an emergency reserve, and conduct a monthly cash-flow review.",
  }),

  cap3Action: Object.freeze({
    "优先投入可验证技能与资产，避免不理解的高风险产品。":
      "Prioritize verifiable skills and assets, and avoid high-risk products you do not understand.",
  }),

  cap4Action: Object.freeze({
    "重大投入前设预算上限、退出条件，并完成独立专业核查。":
      "Before any major commitment, set a budget ceiling and exit conditions, and complete an independent professional review.",
  }),

  cap5Action: Object.freeze({
    "把有效动作写成月度流程，减少情绪化和一次性决策。":
      "Turn effective actions into a monthly process to reduce emotional and one-off decisions.",
  }),
});

export const WEALTH_CAP_ACTION_EXACT_FIELDS = Object.freeze([
  "cap1Action",
  "cap2Action",
  "cap3Action",
  "cap4Action",
  "cap5Action",
]);

export function localizeWealthCapabilityAction(
  locale,
  field,
  value,
) {
  if (typeof value !== "string") return value;
  if (locale !== "en") return value;

  const fieldCatalog = EN_BY_FIELD[field];

  if (!fieldCatalog) return value;

  return Object.prototype.hasOwnProperty.call(
    fieldCatalog,
    value,
  )
    ? fieldCatalog[value]
    : value;
}

export const __WEALTH_CAP_ACTION_EN_TEST_ONLY__ =
  EN_BY_FIELD;

/**
 * I18N-C3-D2 finite components used only by verified Sheet25
 * simple-composite presenters.
 *
 * These maps do not calculate capability ranking or scores.
 * They only resolve already-authoritative finite canonical components.
 */

const D2_RECOMMENDED_MODE_EN = Object.freeze({
  "项目/创业型": "Project / Entrepreneurial",
  "合作/资源整合型": "Collaboration / Resource Integration",
  "专业能力/知识服务型": "Professional Expertise / Knowledge Services",
  "岗位/管理型": "Role / Management",
});

const D2_STRENGTH_BY_LABEL = Object.freeze({
  "开源": Object.freeze({
    canonical: "善于把事业、财富与创业能力转化为收入机会。",
    english:
      "Strong at converting career, wealth, and entrepreneurial capabilities into income opportunities.",
  }),
  "守财": Object.freeze({
    canonical: "重视现金流秩序、资源保留与长期安全垫。",
    english:
      "Values cash-flow discipline, resource retention, and a long-term safety buffer.",
  }),
  "增值": Object.freeze({
    canonical: "善于通过学习、事业积累与项目验证提升长期价值。",
    english:
      "Strong at building long-term value through learning, career accumulation, and project validation.",
  }),
  "风险控制": Object.freeze({
    canonical: "能够在机会与承受能力之间建立判断边界。",
    english:
      "Able to establish decision boundaries between opportunity and risk capacity.",
  }),
  "稳定性": Object.freeze({
    canonical: "具备持续执行、长期积累与制度化复盘基础。",
    english:
      "Has a foundation for sustained execution, long-term accumulation, and systematic review.",
  }),
});

const D2_ACTION_BY_LABEL = Object.freeze({
  "开源": Object.freeze({
    canonical: "聚焦一项可收费价值，明确客户、价格与每周获客动作。",
    english:
      "Focus on one chargeable value proposition, define the target customer and price, and set weekly customer-acquisition actions.",
  }),
  "守财": Object.freeze({
    canonical: "建立固定支出上限、应急储备与月度现金流复盘。",
    english:
      "Set a fixed spending ceiling, build an emergency reserve, and conduct a monthly cash-flow review.",
  }),
  "增值": Object.freeze({
    canonical: "优先投入可验证技能与资产，避免不理解的高风险产品。",
    english:
      "Prioritize verifiable skills and assets, and avoid high-risk products you do not understand.",
  }),
  "风险控制": Object.freeze({
    canonical: "重大投入前设预算上限、退出条件，并完成独立专业核查。",
    english:
      "Before any major commitment, set a budget ceiling and exit conditions, and complete an independent professional review.",
  }),
  "稳定性": Object.freeze({
    canonical: "把有效动作写成月度流程，减少情绪化和一次性决策。",
    english:
      "Turn effective actions into a monthly process to reduce emotional and one-off decisions.",
  }),
});

export function localizeWealthRecommendedMode(
  locale,
  value,
) {
  if (typeof value !== "string") return value;
  if (locale !== "en") return value;

  return Object.prototype.hasOwnProperty.call(
    D2_RECOMMENDED_MODE_EN,
    value,
  )
    ? D2_RECOMMENDED_MODE_EN[value]
    : value;
}

export function resolveWealthCapabilityStrength(
  label,
) {
  if (typeof label !== "string") return undefined;

  return Object.prototype.hasOwnProperty.call(
    D2_STRENGTH_BY_LABEL,
    label,
  )
    ? D2_STRENGTH_BY_LABEL[label]
    : undefined;
}

export function resolveWealthCapabilityAction(
  label,
) {
  if (typeof label !== "string") return undefined;

  return Object.prototype.hasOwnProperty.call(
    D2_ACTION_BY_LABEL,
    label,
  )
    ? D2_ACTION_BY_LABEL[label]
    : undefined;
}

export const __WEALTH_D2_COMPONENTS_TEST_ONLY__ =
  Object.freeze({
    recommendedMode: D2_RECOMMENDED_MODE_EN,
    strengthByLabel: D2_STRENGTH_BY_LABEL,
    actionByLabel: D2_ACTION_BY_LABEL,
  });
